import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, Download } from "lucide-react";
import { checkForUpdate, applyUpdate, currentBuild, newestBuild, subscribeUpdate, isStandalone } from "@/lib/pwa";

const fmtBuild = (b) => (b ? b.replace("T", " ").replace(/:\d\d(\.\d+)?Z?(\+.*)?$/, "") : "development");

// Settings → App version: which build this client is running, and a manual "Check for updates" for installed apps that sit open for days.
export default function AppVersion() {
  const [state, setState] = useState("idle");   // idle | checking | current | update | unknown
  const [build, setBuild] = useState(currentBuild());
  useEffect(() => subscribeUpdate((ready) => { if (ready) setState("update"); }), []);
  useEffect(() => { const t = setTimeout(() => setBuild(currentBuild()), 1500); return () => clearTimeout(t); }, []);
  const check = async () => { setState("checking"); const r = await checkForUpdate({ force: true }); setBuild(currentBuild()); setState(r); };
  return (
    <section className="pp-card p-6 mt-5" data-testid="settings-app-version">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-head text-lg font-extrabold">App version</h2>
          <p className="text-xs text-muted-foreground mt-1">Build <span className="font-mono text-foreground" data-testid="app-version-build">{fmtBuild(build)}</span>{isStandalone() ? " · installed app" : " · browser"}. Installed apps check for updates when you come back to them and every 30 minutes; you can also check now.</p>
        </div>
        <div className="flex items-center gap-2">
          {state === "update"
            ? <button type="button" data-testid="app-version-reload" onClick={applyUpdate} className="pp-btn-dark text-xs py-1.5 inline-flex items-center gap-1"><Download size={12} /> Update now</button>
            : <button type="button" data-testid="app-version-check" onClick={check} disabled={state === "checking"} className="pp-btn-ghost text-xs py-1.5 inline-flex items-center gap-1"><RefreshCw size={12} className={state === "checking" ? "animate-spin" : ""} /> Check for updates</button>}
        </div>
      </div>
      <p className="text-xs mt-2" role="status" data-testid="app-version-status">
        {state === "current" && <span className="text-success-700 font-bold inline-flex items-center gap-1"><CheckCircle2 size={12} aria-hidden="true" /> You're on the latest version.</span>}
        {state === "update" && <span className="text-accent-700 font-bold">A newer build ({fmtBuild(newestBuild())}) is ready — update to switch to it.</span>}
        {state === "unknown" && <span className="text-muted-foreground">Couldn't reach the server to check — try again when you're online.</span>}
      </p>
    </section>
  );
}
